import React, { useRef, useMemo, useState, useEffect, isValidElement } from 'react';
import { safeRest, uniqueId, KEYS } from '@wds/utils';
import { Label } from '@wds/label';
import { IconCheck } from '@wds/icons';
import { Menu } from '@wds/menu';
import { Input } from '@wds/input';
import { Chip } from '@wds/chip';
import { Box } from '@wds/box';
import { CloseButton } from '@wds/internal-components';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
var MultiselectOption = function (_a) {
  var _b = _a.color,
    color = _b === void 0 ? 'primary' : _b,
    children = _a.children,
    _c = _a.dismissible,
    dismissible = _c === void 0 ? false : _c,
    icon = _a.icon,
    _d = _a.value,
    value = _d === void 0 ? '' : _d,
    onClose = _a.onClose,
    _e = _a.disabled,
    disabled = _e === void 0 ? false : _e,
    rest = __rest(_a, ["color", "children", "dismissible", "icon", "value", "onClose", "disabled"]);
  var closeButtonVariant = color === 'muted' || color === 'upgrade' || color === 'info' ? 'dark' : 'light';
  var handleClick = function (e) {
    if (onClose) onClose(e);
  };
  return React.createElement("span", __assign({
    color: color,
    "data-disabled": disabled
  }, safeRest(rest)), icon, value !== null && value !== void 0 ? value : children, dismissible && React.createElement(CloseButton, {
    variant: closeButtonVariant,
    onClick: function (e) {
      return handleClick(e);
    },
    label: "Remove item button."
  }));
};
var useStyles = createMemoStyles(function (theme) {
  var border = theme.border,
    radius = theme.radius,
    palette = theme.palette,
    shadow = theme.shadow,
    spacing = theme.spacing;
  var multiselectStyles = {
    wrenchMultiselectContainer: function (_a) {
      var color = _a.color,
        disabled = _a.disabled;
      return {
        position: 'relative',
        display: 'flex',
        width: '100%',
        minHeight: '40px',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: color ? palette[color].main : palette.border.main,
        background: palette.background.light,
        borderRadius: radius.small,
        pointerEvents: disabled ? 'none' : null,
        opacity: disabled ? '0.4' : null,
        '&:hover, &:focus': {
          borderColor: color ? palette[color].main : palette.border.hover,
          boxShadow: shadow.sky,
          transition: ['all', '100ms']
        },
        '&:focus-within': {
          border: border.active
        }
      };
    },
    wrenchMultiselect: function () {
      return {
        display: 'inline-flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        flexGrow: '1',
        '& > span > input': {
          paddingLeft: spacing[4],
          minWidth: '0',
          border: 'none',
          flexGrow: '1'
        },
        '& > span > input:hover': {
          boxShadow: 'none'
        },
        // the menu
        '& > span > div': {
          width: '100%',
          maxWidth: 'none',
          top: 'auto',
          left: 0,
          marginTop: '1px'
        },
        // the Chips
        '& > div > div': {
          marginLeft: spacing[2],
          marginTop: spacing[2]
        },
        // list item icons
        '& [role="menuitem"] > span > svg': {
          marginRight: spacing[3]
        },
        // styles the Menu container
        '& > span': {
          position: 'initial',
          flexGrow: '1',
          '& > input': {
            width: '100%'
          }
        }
      };
    },
    wrenchMultiselectAddon: function () {
      return {
        padding: [spacing[2], spacing[4], 0, spacing[4]]
      };
    },
    wrenchMultiselectChecked: function () {
      return {
        '& > svg': {
          width: '1.2em',
          height: '1.2em'
        }
      };
    }
  };
  return multiselectStyles;
});
var Multiselect = function (_a) {
  var _b = _a.show,
    show = _b === void 0 ? false : _b,
    color = _a.color,
    children = _a.children,
    addOn = _a.addOn,
    _c = _a.addOnPlacement,
    addOnPlacement = _c === void 0 ? 'left' : _c,
    _d = _a.autoClose,
    autoClose = _d === void 0 ? true : _d,
    id = _a.id,
    label = _a.label,
    _e = _a.disabled,
    disabled = _e === void 0 ? false : _e,
    placeholder = _a.placeholder,
    _f = _a.removeFromMenu,
    removeFromMenu = _f === void 0 ? false : _f,
    inputProps = _a.inputProps,
    selectedValues = _a.selectedValues,
    _g = _a.useChips,
    useChips = _g === void 0 ? true : _g,
    _h = _a.readOnly,
    readOnly = _h === void 0 ? false : _h,
    onAdd = _a.onAdd,
    onRemove = _a.onRemove,
    onMenuChange = _a.onMenuChange,
    _j = _a.autocomplete,
    autocomplete = _j === void 0 ? true : _j,
    renderTo = _a.renderTo,
    elevation = _a.elevation,
    rest = __rest(_a, ["show", "color", "children", "addOn", "addOnPlacement", "autoClose", "id", "label", "disabled", "placeholder", "removeFromMenu", "inputProps", "selectedValues", "useChips", "readOnly", "onAdd", "onRemove", "onMenuChange", "autocomplete", "renderTo", "elevation"]);
  var _k = useStyles({
      color: color,
      disabled: disabled
    }),
    wrenchMultiselectContainer = _k.wrenchMultiselectContainer,
    wrenchMultiselect = _k.wrenchMultiselect,
    wrenchMultiselectChecked = _k.wrenchMultiselectChecked,
    wrenchMultiselectAddon = _k.wrenchMultiselectAddon;
  var value = inputProps === null || inputProps === void 0 ? void 0 : inputProps.value;
  var defaultValue = inputProps === null || inputProps === void 0 ? void 0 : inputProps.defaultValue;
  var inputRef = useRef(null);
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('input');
  }, [id]);
  var multiselectID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('multiselect');
  }, [id]);
  var childOptions = React.Children.toArray(children);
  var _l = useState(childOptions),
    options = _l[0],
    setOptions = _l[1];
  var _m = useState(show),
    showMenu = _m[0],
    setShowMenu = _m[1];
  var _o = useState(value !== null && value !== void 0 ? value : ''),
    filterBy = _o[0],
    setFilterBy = _o[1];
  var initialOptions = selectedValues ? selectedValues.map(function (val, index) {
    return val ? index : null;
  }) : options.map(function (child, index) {
    return child.props.selected ? index : null;
  });
  var _p = useState(initialOptions),
    selectedOptions = _p[0],
    setSelectedOptions = _p[1];
  var getSelectedOrder = selectedOptions.filter(function (index) {
    return index !== null;
  });
  var _q = useState(getSelectedOrder),
    selectedOrder = _q[0],
    setSelectedOrder = _q[1];
  /**
   * Allows selected items to be controlled by props
   */
  useEffect(function () {
    // update all internal state to match the controlled prop selectedValues
    if (selectedValues) {
      var tempSelectedOptions = selectedValues.map(function (val, index) {
        return val ? index : null;
      });
      setSelectedOptions(tempSelectedOptions);
      setSelectedOrder(tempSelectedOptions.filter(function (index) {
        return index !== null;
      }));
    }
  }, [selectedValues]);
  /**
   * Allows show to be controlled by props
   */
  useEffect(function () {
    setShowMenu(show);
  }, [show]);
  var handleMenuChange = function () {
    if (onMenuChange) {
      onMenuChange(showMenu);
    }
  };
  var addChip = function (index) {
    var newChip = React.cloneElement(options[index], {
      selected: true
    });
    setOptions(function (prevOptions) {
      return prevOptions.map(function (option, i) {
        return i === index ? newChip : option;
      });
    });
    setSelectedOrder(function (prevSelectedOrder) {
      return __spreadArray(__spreadArray([], prevSelectedOrder, true), [index], false);
    });
    setFilterBy('');
    if (inputRef.current != null) {
      inputRef.current.value = '';
    }
    setShowMenu(!autoClose);
    if (onAdd) onAdd(index);
  };
  var removeChip = function (index) {
    var removedChip = React.cloneElement(options[index], {
      selected: false
    });
    setOptions(function (prevOptions) {
      return prevOptions.map(function (option, i) {
        return i === index ? removedChip : option;
      });
    });
    setSelectedOrder(function (prevSelectedOrder) {
      return prevSelectedOrder.filter(function (i) {
        return i !== index;
      });
    });
    setShowMenu(!autoClose);
    if (onRemove) onRemove(index);
  };
  var getChip = function (child, index) {
    if (!isValidElement(child))
      // return null here for linting rule requirements
      return React.createElement(React.Fragment, null, null);
    var optionId = "multiselect-child-".concat(index);
    return React.createElement(Chip, {
      key: optionId,
      "data-optionid": optionId,
      "data-index": index,
      onClick: function () {
        return removeChip(index);
      },
      variant: "dismissible",
      size: "sm",
      icon: child.props.icon
    }, React.createElement(Box, {
      element: "span"
    }, child.props.children));
  };
  var clickMenuItem = function (index, selected) {
    if (selected) {
      removeChip(index);
    } else {
      addChip(index);
    }
  };
  var getMenuItem = function (child, index, selected) {
    var _a;
    if (!isValidElement(child))
      // return null here for linting rule requirements
      return React.createElement(React.Fragment, null, null);
    var optionId = "multiselect-child-".concat(index);
    var itemText = (_a = child.props.children) !== null && _a !== void 0 ? _a : child.props.value;
    var filterByStr = filterBy;
    var autocompleteMatch = typeof itemText === 'string' && itemText.toUpperCase().includes(filterByStr.toUpperCase());
    var shouldHide = autocomplete && !autocompleteMatch;
    var shouldDisable = child.props.disabled && shouldHide;
    if (!autocompleteMatch) {
      // return null here for linting rule requirements
      return React.createElement(React.Fragment, null, null);
    }
    return React.createElement(Menu.Item, {
      key: optionId,
      id: optionId,
      disabled: shouldDisable,
      onClick: function () {
        return clickMenuItem(index, selected);
      },
      addOn: React.createElement("span", {
        className: wrenchMultiselectChecked
      }, selected && React.createElement(IconCheck, {
        title: "Checkmark icon",
        color: "success"
      }))
    }, child.props.icon, child.props.children);
  };
  var handleKeyDown = function (e) {
    var el = e.target;
    var emptyInput = el.value === '';
    var nothingSelected = selectedOrder.length !== 0;
    var inputEl = inputRef.current;
    switch (e.key) {
      case KEYS.DOWN:
        e.preventDefault();
        if (showMenu) {
          var inputElSibling = inputEl.nextElementSibling;
          var menuItems_1 = inputElSibling.getElementsByTagName('li');
          if (menuItems_1[0]) {
            var menuItemChild = menuItems_1[0].firstChild;
            if (menuItemChild) {
              menuItemChild.focus();
            }
          }
        }
        break;
      case KEYS.BACKSPACE:
        if (emptyInput && nothingSelected) {
          removeChip(selectedOrder[selectedOrder.length - 1]);
        }
        break;
      case KEYS.ESC:
        inputEl.value = '';
        setFilterBy('');
        break;
      default:
        if (autocomplete) {
          if (!showMenu) {
            setShowMenu(true);
          }
          setFilterBy(el.value);
        }
        break;
    }
  };
  var inputTrigger = React.createElement(Input, __assign({}, value === undefined || value === null ? {
    defaultValue: defaultValue
  } : {
    value: value
  }, {
    id: inputID,
    ref: inputRef,
    placeholder: placeholder,
    readOnly: readOnly,
    onBlur: function (e) {
      var _a;
      if (!((_a = e.currentTarget.nextSibling) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
        setShowMenu(false);
      }
    },
    onKeyDown: function (e) {
      handleKeyDown(e);
      if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onKeyDown) inputProps.onKeyDown(e);
    },
    onKeyUp: function (e) {
      if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onKeyUp) inputProps.onKeyUp(e);
    },
    onChange: function (e) {
      if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onChange) inputProps.onChange(e);
    }
  }));
  var renderAddOn = function () {
    if (addOn) {
      return React.createElement("div", {
        className: wrenchMultiselectAddon
      }, addOn);
    }
    // return null here for linting rule requirements
    return React.createElement(React.Fragment, null, null);
  };
  /**
   * Generates a new array of selected menu items, these are the Chips
   * that appear in the Multiselect input
   */
  var selectedMenuItems = selectedOrder.map(function (order) {
    return order !== null && getChip(options[order], order);
  });
  /**
   * Generates menu items, these appear in the Menu when the multiselect
   * is focused, it also removes any that don't match the filter for
   * autocomplete
   */
  var menuItems = options.map(function (child, index) {
    if (selectedValues) {
      // controlled multiselect
      return getMenuItem(child, index, selectedValues[index]);
    }
    if (!child.props.selected) {
      return getMenuItem(child, index, false);
    }
    if (!removeFromMenu && child.props.selected) {
      return getMenuItem(child, index, child.props.selected);
    }
    return null;
  }).filter(function (item) {
    return item !== '';
  });
  return React.createElement(React.Fragment, null, label && React.createElement(Label, {
    htmlFor: inputID
  }, label), React.createElement("div", __assign({
    id: multiselectID,
    className: wrenchMultiselectContainer,
    "data-selection": selectedMenuItems
  }, safeRest(rest)), addOnPlacement === 'left' && renderAddOn(), React.createElement("div", {
    className: wrenchMultiselect
  }, useChips && selectedMenuItems, React.createElement(Menu, {
    autoClose: autoClose,
    dense: true,
    noRules: true,
    menuTrigger: inputTrigger,
    show: showMenu,
    onChange: handleMenuChange,
    renderTo: renderTo,
    elevation: elevation
  }, menuItems)), addOnPlacement === 'right' && renderAddOn()));
};
Multiselect.Option = MultiselectOption;
export { Multiselect };